import React, { useState, useEffect, useRef, useCallback } from "react";
import { THEME } from "../theme";
import { Badge } from "./UIComponents";
import { shouldUseRelativeBackendProxy } from "../lib/backendAvailability";

const STATUS_COLORS = {
  pending: THEME.warning,
  processing: THEME.accent,
  completed: THEME.success,
  failed: THEME.danger,
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function DocumentManager() {
  const apiBaseUrl = (typeof import.meta !== "undefined" ? import.meta.env?.VITE_API_BASE_URL : "") || "";
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState("");
  const [backendOff, setBackendOff] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    if (!shouldUseRelativeBackendProxy()) {
      setBackendOff(true);
      setDocuments([]);
      setError("");
      setLoading(false);
      return;
    }
    setBackendOff(false);
    setLoading(true);
    try {
      const url = apiBaseUrl ? `${apiBaseUrl.replace(/\/$/, "")}/api/documents/?limit=50` : "/api/documents/?limit=50";
      const resp = await fetch(url);
      const text = await resp.text();
      if (resp.status >= 500) {
        if (import.meta.env?.DEV) {
          console.debug("Document API 500", url);
        }
        setDocuments([
          {
            id: "cached-mock",
            title: "Screening document (placeholder)",
            status: "completed",
            source: "Cached",
            created_at: new Date().toISOString(),
          },
        ]);
        setError("Failed (server). Using fallback. Source: document API (see console).");
        return;
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = JSON.parse(text);
      setDocuments(data.documents || []);
      setError("");
    } catch (e) {
      if (import.meta.env?.DEV) {
        console.debug("Document list fetch", e);
      }
      setError("Could not reach the document API. Start FastAPI on port 8000 or set VITE_API_BASE_URL.");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const uploadFile = async (file) => {
    if (!shouldUseRelativeBackendProxy()) {
      setError("Document API is disabled. Remove VITE_SKIP_BACKEND_PROXY from .env or set VITE_API_BASE_URL, then restart Vite.");
      return;
    }
    setUploading(true);
    setError("");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const root = (apiBaseUrl || "").replace(/\/$/, "");
      const uploadUrl = root ? `${root}/api/documents/upload` : "/api/documents/upload";
      const resp = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || "Upload failed");
      }
      await fetchDocuments();
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
  };

  const ingestUrl = async () => {
    if (!urlInput.trim()) return;
    if (!shouldUseRelativeBackendProxy()) {
      setError("Document API is disabled. Remove VITE_SKIP_BACKEND_PROXY from .env or set VITE_API_BASE_URL, then restart Vite.");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const root = (apiBaseUrl || "").replace(/\/$/, "");
      const ingestU = root ? `${root}/api/documents/ingest-url` : "/api/documents/ingest-url";
      const resp = await fetch(ingestU, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: urlInput.trim() }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || "URL ingestion failed");
      }
      setUrlInput("");
      await fetchDocuments();
    } catch (err) {
      setError(err.message);
    }
    setUploading(false);
  };

  const deleteDocument = async (id, filename) => {
    if (!window.confirm(`Delete "${filename}"? This will remove the document and all its chunks.`)) return;
    if (!shouldUseRelativeBackendProxy()) return;
    setError("");
    try {
      const root = (apiBaseUrl || "").replace(/\/$/, "");
      const delU = root ? `${root}/api/documents/${id}` : `/api/documents/${id}`;
      const resp = await fetch(delU, { method: "DELETE" });
      if (!resp.ok) throw new Error("Delete failed");
      await fetchDocuments();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  };

  return (
    <div className="fade-in" style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 960, margin: "0 auto" }}>
      {backendOff && (
        <div
          className="rounded-ds-xl border border-border bg-canvas-subtle px-4 py-3 text-sm text-fg-secondary"
          role="status"
        >
          Document API requests are off because <code className="font-mono text-xs">VITE_SKIP_BACKEND_PROXY=1</code> is set. Remove it (dev defaults to the Vite proxy on port 8000) or set{" "}
          <code className="font-mono text-xs">VITE_API_BASE_URL</code>, then restart Vite. Ensure FastAPI is running on <code className="font-mono text-xs">127.0.0.1:8000</code>.
        </div>
      )}
      {/* ─── Upload Section ─── */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: THEME.accent, marginBottom: 16, letterSpacing: "0.08em" }}>
          UPLOAD DOCUMENTS
        </div>

        {/* Drag & Drop Zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          style={{
            border: `2px dashed ${dragOver ? THEME.accent : THEME.border}`,
            borderRadius: 12,
            padding: "32px 24px",
            textAlign: "center",
            cursor: "pointer",
            background: dragOver ? THEME.accentGlow : THEME.bgPanel,
            transition: "all 0.2s",
            marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
          <div style={{ fontSize: 13, color: THEME.text, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
            {uploading ? "Processing document..." : "Drop a file here or click to browse"}
          </div>
          <div style={{ fontSize: 11, color: THEME.textMuted, marginTop: 4 }}>
            Supports PDF, DOCX, PPTX, HTML and other document formats
          </div>
          <input ref={fileInputRef} type="file" onChange={handleFileChange} style={{ display: "none" }} />
        </div>

        {/* URL Ingestion */}
        <div style={{ display: "flex", gap: 8 }}>
          <input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && ingestUrl()}
            placeholder="Or paste a document URL to ingest..."
            style={{
              flex: 1, padding: "10px 14px", borderRadius: 8,
              border: `1px solid ${THEME.border}`, background: THEME.bgPanel,
              color: THEME.text, fontSize: 13, outline: "none", fontFamily: "inherit",
            }}
          />
          <button
            onClick={ingestUrl}
            disabled={uploading || !urlInput.trim()}
            style={{
              padding: "10px 20px", borderRadius: 8, border: "none",
              background: THEME.accent, color: "#fff", fontSize: 13,
              fontWeight: 600, cursor: uploading ? "not-allowed" : "pointer",
              opacity: uploading || !urlInput.trim() ? 0.5 : 1,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Ingest URL
          </button>
        </div>

        {uploading && (
          <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: THEME.accentGlow, border: `1px solid ${THEME.accent}30`, fontSize: 12, color: THEME.accent }}>
            <span style={{ animation: "pulse 1s infinite" }}>⏳ Processing document — chunking, embedding, and indexing...</span>
          </div>
        )}

        {error && (
          <div style={{ marginTop: 12, padding: "8px 12px", borderRadius: 8, background: THEME.dangerGlow, border: `1px solid ${THEME.danger}30`, fontSize: 12, color: THEME.danger }}>
            ⚠️ {error}
          </div>
        )}
      </div>

      {/* ─── Document List ─── */}
      <div className="card" style={{ padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: THEME.accent, letterSpacing: "0.08em" }}>
            DOCUMENT LIBRARY ({documents.length})
          </div>
          <button
            onClick={fetchDocuments}
            disabled={loading}
            style={{
              padding: "4px 12px", borderRadius: 6, border: `1px solid ${THEME.border}`,
              background: "transparent", color: THEME.textMuted, fontSize: 11,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            ↻ Refresh
          </button>
        </div>

        <p
          style={{
            marginBottom: 16,
            padding: "10px 14px",
            borderRadius: 8,
            border: `1px solid ${THEME.border}`,
            background: THEME.bgPanel,
            fontSize: 12,
            color: THEME.textMuted,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          <strong style={{ color: THEME.text }}>RAG</strong> — document search will be added in a future update.
        </p>

        {loading && documents.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, color: THEME.textMuted, fontSize: 13 }}>
            Loading documents...
          </div>
        )}

        {!loading && documents.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, color: THEME.textMuted, fontSize: 13 }}>
            No documents uploaded yet. Upload a file or paste a URL above to get started.
          </div>
        )}

        {documents.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {documents.map((doc) => (
              <div
                key={doc.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto auto auto",
                  gap: 16,
                  alignItems: "center",
                  padding: "12px 16px",
                  borderRadius: 8,
                  border: `1px solid ${THEME.border}`,
                  background: THEME.bgPanel,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: 600, color: THEME.text,
                    fontFamily: "'DM Sans', sans-serif",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    📄 {doc.filename}
                  </div>
                  <div style={{ fontSize: 10, color: THEME.textDim, marginTop: 2 }}>
                    Uploaded {formatDate(doc.uploaded_at)}
                  </div>
                </div>

                <Badge color={STATUS_COLORS[doc.status] || THEME.textMuted}>
                  {doc.status.toUpperCase()}
                </Badge>

                <div style={{ textAlign: "right", minWidth: 60 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: THEME.text }}>{doc.page_count}</div>
                  <div style={{ fontSize: 9, color: THEME.textDim }}>pages</div>
                </div>

                <div style={{ textAlign: "right", minWidth: 60 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: THEME.text }}>{doc.chunk_count}</div>
                  <div style={{ fontSize: 9, color: THEME.textDim }}>chunks</div>
                </div>

                <button
                  onClick={() => deleteDocument(doc.id, doc.filename)}
                  title="Delete document"
                  style={{
                    width: 32, height: 32, borderRadius: 6, border: `1px solid ${THEME.border}`,
                    background: "transparent", color: THEME.danger, fontSize: 14,
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.2s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = THEME.dangerGlow; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  🗑
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
